import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
  Box,
  Text,
  Button,
  Flex,
  Grid,
  Image,
  InputGroup,
  Field,
  IconButton,
} from '@chakra-ui/react';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import PhoneInput, {
  isValidPhoneNumber,
  type Value as PhoneValue,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { takeHandoffJwt } from '@aegix/auth-core';
import { useAuthActions } from '@aegix/auth-identity';
import type { SMSUser } from '../../lib/sms-auth';
import { signIn } from '../../services/auth.service';
import { toaster } from '../../components/ui/toaster';
import { ColorModeButton } from '../../components/ui/color-mode';
import { openSsoWindow } from '../../lib/openSsoWindow';
import { deviceIdFetch } from '../../lib/deviceIdHeader';
import aegixLogo from '../../assets/aegix-beacon.svg';
import lockIcon from '../../assets/lockIcon.svg';
import { InputField } from '@themes/aegix';
import { loginPasswordSchema } from '../../validations/login.validation';

interface LoginLocationResponse {
  login_url: string;
  sso: boolean;
  type?: 'saml' | 'oidc';
  is_location_login: boolean;
  has_temporary_user_details: boolean;
}

export function Login() {
  usePageTitle('Sign In');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithToken } = useAuthActions<SMSUser>();
  const redirectTo = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  // Shared ("location") password login — the precheck's
  // `is_location_login` flips on the identity-capture fields the backend
  // mints the temporary child sub-user from.
  const [isLocationLogin, setIsLocationLogin] = useState(false);
  // Shared ("location") SSO login — the precheck said
  // sso && is_location_login, so the identity fields render WITHOUT the
  // password field and the second submit re-POSTs the precheck WITH the
  // fields before the popup opens (mirrors AIM's two-submit SSO flow).
  const [showSsoIdentityFields, setShowSsoIdentityFields] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cellPhone, setCellPhone] = useState<PhoneValue | undefined>(undefined);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    cell_phone: '',
  });
  // Both shared-login branches — password and SSO —
  // capture the same identity fields behind the same validation gate.
  const showIdentityCapture =
    (showPasswordField || showSsoIdentityFields) && isLocationLogin;

  // Handle the SSO handoff token (fallback when the popup can't reach its
  // opener). Taken from `@aegix/auth-core`, not the URL: `main.tsx` consumes
  // the token out of the address bar before `Sentry.init`, because Session
  // Replay records `location.search`/`href` synchronously at init and the
  // replay envelope never passes through `beforeSend`.
  //
  // `takeHandoffJwt()` clears the token, so it is submitted at most once per
  // page load: a remount after sign-out, a deps change or StrictMode's second
  // effect pass finds nothing to take.
  useEffect(() => {
    const jwt = takeHandoffJwt();
    if (!jwt) return;

    if (!loginWithToken) {
      // Should not happen — SMS uses simpleJwtAdapter which implements
      // loginWithToken. Defensive guard kept in case the adapter ever
      // changes; surfaces the misconfiguration as a clear error.
      toaster.create({
        title: 'Login failed',
        description: 'SSO authentication is not configured',
        type: 'error',
        duration: 5000,
      });
      return;
    }
    setIsLoading(true);
    loginWithToken(jwt)
      .then(() => {
        navigate(redirectTo, { replace: true });
      })
      .catch(() => {
        setIsLoading(false);
        toaster.create({
          title: 'Login failed',
          description: 'SSO authentication failed',
          type: 'error',
          duration: 5000,
        });
      });
  }, [loginWithToken, navigate, redirectTo]);

  const validate = () => {
    const newErrors = {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      cell_phone: '',
    };
    let isValid = true;

    if (!email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (showPasswordField) {
      // Non-empty only — a login form must not enforce
      // password-composition policy; the server is the authenticator.
      const passwordResult = loginPasswordSchema.safeParse(password);
      if (!passwordResult.success) {
        newErrors.password = passwordResult.error.issues[0].message;
        isValid = false;
      }
    }

    // The location sign-in identity fields are the payload
    // the backend mints the temporary child sub-user from. Block submit with
    // inline errors until the names are present and the phone parses — the
    // backend precheck 400s on missing fields, and an invalid phone is
    // silently dropped on the login paths (matches AIM's location sign-in gate).
    // The SSO branch shares the gate — validation must pass
    // before the second precheck fires and the popup opens.
    if (showIdentityCapture) {
      if (!firstName.trim()) {
        newErrors.first_name = 'First name is required';
        isValid = false;
      }
      if (!lastName.trim()) {
        newErrors.last_name = 'Last name is required';
        isValid = false;
      }
      if (!cellPhone) {
        newErrors.cell_phone = 'Phone number is required';
        isValid = false;
      } else if (!isValidPhoneNumber(String(cellPhone))) {
        newErrors.cell_phone = 'Enter a valid phone number';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // The optional `identity` fields ride the SECOND precheck of a
  // shared ("location") SSO login. The backend stashes them server-side and
  // embeds a single-use nonce in the returned `login_url` — the
  // client adds NOTHING to the URL itself.
  const checkSso = async (identity?: {
    first_name: string;
    last_name: string;
    cell_phone: string;
  }): Promise<LoginLocationResponse> => {
    const baseURL = window.__ENV?.NX_API_URL || '';
    const redirectOrigin = encodeURIComponent(window.location.origin);
    // `deviceIdFetch` stamps `x-device-id` — the backend forwards
    // it into SAML RelayState / OIDC state so the SSO-minted session is
    // bound to this browser's device identity.
    const response = await deviceIdFetch(
      `${baseURL}/rest/login-location/?mobile=false&redirect_origin=${redirectOrigin}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...identity }),
      },
    );

    if (!response.ok) {
      throw new Error('Failed to check login method');
    }

    return response.json();
  };

  const handleSsoLogin = (loginUrl: string) => {
    openSsoWindow(loginUrl, async (jwt: string) => {
      if (!loginWithToken) {
        toaster.create({
          title: 'Login failed',
          description: 'SSO authentication is not configured',
          type: 'error',
          duration: 5000,
        });
        return;
      }
      try {
        await loginWithToken(jwt);
        toaster.create({
          title: 'Login successful',
          description: 'Welcome back!',
          type: 'success',
          duration: 3000,
        });
        navigate(redirectTo);
      } catch {
        toaster.create({
          title: 'Login failed',
          description: 'SSO authentication failed',
          type: 'error',
          duration: 5000,
        });
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // First submit: check SSO before showing password field
    if (!showPasswordField && !showSsoIdentityFields) {
      setIsLoading(true);
      try {
        const data = await checkSso();
        if (data.sso) {
          setIsLoading(false);
          if (data.is_location_login) {
            // A shared ("location") SSO login must carry
            // first/last/phone into the IdP stash the backend mints the
            // temporary child sub-user from — capture them first and hold
            // the popup until the second precheck has forwarded them.
            setIsLocationLogin(true);
            setShowSsoIdentityFields(true);
          } else {
            // SSO enabled, personal account — open popup
            handleSsoLogin(data.login_url);
          }
        } else {
          // No SSO — show password field. A location (shared) login
          // additionally reveals the identity-capture fields.
          setIsLoading(false);
          setIsLocationLogin(data.is_location_login);
          setShowPasswordField(true);
        }
      } catch {
        // SSO check failed — fall back to showing password field
        setIsLoading(false);
        setShowPasswordField(true);
      }
      return;
    }

    // Second submit, SSO shared login: re-POST the precheck WITH
    // the identity fields — the backend stashes them and embeds a single-use
    // nonce in the returned login_url — then open the popup with
    // that URL untouched.
    if (showSsoIdentityFields) {
      setIsLoading(true);
      try {
        const data = await checkSso({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          cell_phone: String(cellPhone),
        });
        setIsLoading(false);
        if (data.sso) {
          handleSsoLogin(data.login_url);
        } else {
          // IdP config changed between submits — fall back to the password
          // branch (any captured identity fields stay filled).
          setShowSsoIdentityFields(false);
          setIsLocationLogin(data.is_location_login);
          setShowPasswordField(true);
        }
      } catch {
        setIsLoading(false);
        toaster.create({
          title: 'Login failed',
          description: 'Failed to check login method',
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    // Second submit: email/password login
    setIsLoading(true);

    try {
      if (isLocationLogin) {
        // A shared ("location") password login must carry
        // first_name/last_name/cell_phone in the `POST /rest/sessions` body —
        // `SessionCreate` prechecks role users (400 without them) and mints
        // the temporary child sub-user from them. The shared SimpleJWT
        // adapter posts `{email, password}` only, so this branch posts the
        // 5-field body via the `signIn` service and adopts the child-bound
        // session via `loginWithToken` (the SSO path's session-adoption
        // surface: it fetches `/me/` as the child and persists the child
        // JWT). Mirrors AIM's location sign-in integration.
        if (!loginWithToken) {
          throw new Error('Login failed');
        }
        const session = await signIn({
          email,
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          cell_phone: String(cellPhone),
        });
        if (!session.jwt) {
          throw new Error('Login failed');
        }
        await loginWithToken(session.jwt);
      } else {
        // ADR-118 — lib's LoginCredentials is `{ email, password }` rather
        // than the legacy positional `(email, password)` shape.
        await login({ email, password });
      }

      toaster.create({
        title: 'Login successful',
        description: `Welcome back!`,
        type: 'success',
        duration: 3000,
      });

      navigate(redirectTo);
    } catch (error) {
      toaster.create({
        title: 'Login failed',
        description:
          error instanceof Error ? error.message : 'Invalid credentials',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      w="100%"
      justifyContent="center"
      alignItems="center"
      minH="100vh"
      bg="surface.primary"
      position="relative"
      data-e2e="loginPageContainer"
    >
      {/* Color mode toggle */}
      <ColorModeButton
        data-e2e="darkLightTheme"
        position="absolute"
        top="20px"
        right="20px"
      />

      <Grid maxW={{ base: '100%', sm: '448px' }} w="100%" px={{ base: 4, sm: 0 }}>
        <Flex justifyContent="center" flexDirection="column">
          <Grid gap="24px" paddingBottom="32px">
            <Flex justifyContent="center">
              <Image
                src={aegixLogo}
                alt="AEGIX"
                height="80px"
                width="80px"
                objectFit="contain"
                data-e2e="loginPageImage"
              />
            </Flex>
            <Text
              as="h1"
              data-e2e="loginTextHeader"
              textAlign="center"
              fontSize="30px"
              fontWeight={700}
              color="text.primary"
            >
              Sign in to your account
            </Text>
          </Grid>

          <form onSubmit={handleSubmit} data-e2e="loginForm" aria-label="Sign in">
            <Grid gap="24px">
              <Box>
                <Field.Root invalid={!!errors.email}>
                  <Field.Label htmlFor="email" srOnly>Email address</Field.Label>
                  <InputField
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setShowPasswordField(false);
                      setIsLocationLogin(false);
                      setShowSsoIdentityFields(false);
                    }}
                    autoComplete="email"
                    placeholder="Email address"
                    data-e2e="loginEmail"
                    bg="surface.primary"
                    // The joined-corner pair only applies when the password
                    // input sits directly below; the location identity fields
                    // break the pair, so both go back to full radius.
                    borderRadius={
                      showPasswordField && !isLocationLogin
                        ? '5px 5px 0px 0px'
                        : '8px'
                    }
                    height="38px"
                    w="100%"
                    padding="0px 12px"
                    color="text.primary"
                    aria-invalid={!!errors.email || undefined}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    borderColor={errors.email ? 'border.danger' : undefined}
                  />
                  {errors.email && (
                    <Field.ErrorText id="email-error" data-e2e="login-email-error">
                      {errors.email}
                    </Field.ErrorText>
                  )}
                </Field.Root>
                {showIdentityCapture && (
                  <Flex
                    flexDirection="column"
                    gap="3"
                    mt="3"
                    data-e2e="loginLocationFields"
                  >
                    <Field.Root invalid={!!errors.first_name}>
                      <Field.Label htmlFor="first_name" srOnly>
                        First name
                      </Field.Label>
                      <InputField
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- location sign-in step; focuses the first newly revealed input
                        autoFocus
                        id="first_name"
                        name="first_name"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        placeholder="First name"
                        data-e2e="loginFirstName"
                        bg="surface.primary"
                        borderRadius="8px"
                        height="38px"
                        w="100%"
                        padding="0px 12px"
                        color="text.primary"
                        aria-invalid={!!errors.first_name || undefined}
                        aria-describedby={
                          errors.first_name ? 'first-name-error' : undefined
                        }
                        borderColor={
                          errors.first_name ? 'border.danger' : undefined
                        }
                      />
                      {errors.first_name && (
                        <Field.ErrorText
                          id="first-name-error"
                          data-e2e="login-first-name-error"
                        >
                          {errors.first_name}
                        </Field.ErrorText>
                      )}
                    </Field.Root>
                    <Field.Root invalid={!!errors.last_name}>
                      <Field.Label htmlFor="last_name" srOnly>
                        Last name
                      </Field.Label>
                      <InputField
                        id="last_name"
                        name="last_name"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        placeholder="Last name"
                        data-e2e="loginLastName"
                        bg="surface.primary"
                        borderRadius="8px"
                        height="38px"
                        w="100%"
                        padding="0px 12px"
                        color="text.primary"
                        aria-invalid={!!errors.last_name || undefined}
                        aria-describedby={
                          errors.last_name ? 'last-name-error' : undefined
                        }
                        borderColor={
                          errors.last_name ? 'border.danger' : undefined
                        }
                      />
                      {errors.last_name && (
                        <Field.ErrorText
                          id="last-name-error"
                          data-e2e="login-last-name-error"
                        >
                          {errors.last_name}
                        </Field.ErrorText>
                      )}
                    </Field.Root>
                    <Field.Root invalid={!!errors.cell_phone}>
                      <Box
                        w="100%"
                        data-e2e="loginCellPhone"
                        css={{
                          '& .PhoneInput': { width: '100%' },
                          '& .PhoneInputInput': {
                            width: '100%',
                            height: '38px',
                            padding: '0px 12px',
                            borderRadius: '8px',
                            backgroundColor:
                              'var(--chakra-colors-surface-primary)',
                            color: 'var(--chakra-colors-text-primary)',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: errors.cell_phone
                              ? 'var(--chakra-colors-border-danger)'
                              : 'var(--chakra-colors-border-secondary)',
                            outline: 'none',
                            '&:focus': {
                              borderColor:
                                'var(--chakra-colors-border-primary)',
                            },
                          },
                          '& .PhoneInputCountry': { marginRight: '8px' },
                          '& .PhoneInputCountrySelect': {
                            backgroundColor:
                              'var(--chakra-colors-surface-primary)',
                            color: 'var(--chakra-colors-text-primary)',
                          },
                        }}
                      >
                        <PhoneInput
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="US"
                          value={cellPhone}
                          onChange={(value) => {
                            setCellPhone(value || undefined);
                            // The inline error reflects the last submit;
                            // clear it as soon as the value changes.
                            setErrors((prev) => ({ ...prev, cell_phone: '' }));
                          }}
                          placeholder="Cell number"
                          name="cell_phone"
                          autoComplete="tel"
                          // Effectively required — the backend mints the
                          // temporary sub-user from this number and its
                          // precheck 400s when it's missing.
                          required
                        />
                      </Box>
                      {errors.cell_phone && (
                        <Field.ErrorText
                          id="cell-phone-error"
                          data-e2e="login-cell-phone-error"
                        >
                          {errors.cell_phone}
                        </Field.ErrorText>
                      )}
                    </Field.Root>
                  </Flex>
                )}
                {showPasswordField && (
                  <Field.Root invalid={!!errors.password} mt={isLocationLogin ? '3' : undefined}>
                    <Field.Label htmlFor="password" srOnly>Password</Field.Label>
                    <InputGroup
                      endElement={
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword(!showPassword)}
                          variant="ghost"
                          size="xs"
                          color="text.secondary"
                        >
                          {showPassword ? <HiEye /> : <HiEyeOff />}
                        </IconButton>
                      }
                    >
                      <InputField
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- login flow password step; focuses the single next required input (the location variant focuses first_name instead)
                        autoFocus={!isLocationLogin}
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        placeholder="Password"
                        data-e2e="loginPassword"
                        bg="surface.primary"
                        borderRadius={
                          isLocationLogin ? '8px' : '0px 0px 5px 5px'
                        }
                        h="38px"
                        w="100%"
                        px="12px"
                        color="text.primary"
                        aria-invalid={!!errors.password || undefined}
                        aria-describedby={errors.password ? 'password-error' : undefined}
                        borderColor={errors.password ? 'border.danger' : undefined}
                      />
                    </InputGroup>
                    {errors.password && (
                      <Field.ErrorText id="password-error" data-e2e="login-password-error">
                        {errors.password}
                      </Field.ErrorText>
                    )}
                  </Field.Root>
                )}
              </Box>

              <Box data-e2e="loginButtonWrapper" height="40px">
                <Button
                  type="submit"
                  w="100%"
                  h="100%"
                  borderRadius="8px"
                  bg="button.primary"
                  color="white"
                  data-e2e="loginButton"
                  padding="12px 6px"
                  border="1px solid"
                  borderColor="border.main"
                  disabled={isLoading}
                  loading={isLoading}
                  _hover={{
                    bg: 'button.primary',
                    opacity: 0.8,
                  }}
                >
                  <Flex justifyContent="left" width="100%">
                    <Image src={lockIcon} mr={2} />
                    <Text width="100%">Sign in</Text>
                  </Flex>
                </Button>
              </Box>
            </Grid>
          </form>
        </Flex>
      </Grid>
    </Box>
  );
}
